import React, { Fragment, ReactNode } from 'react';
import { Link } from 'react-static';
import styled from 'styled-components';
import em from 'polished/lib/helpers/em';
import { isNew } from '@flow/utils/helperUtils';
import { Badge } from '@flow/components/ui/';
import { getRelativeUrl } from '@flow/utils/cleaners';
import FlowStripe from '@flow/components/ticker/FlowStripe';
import { Waypoint } from 'react-waypoint';
import isRetina from 'is-retina';
import { getArtTagBadges } from '@flow/scenes/arts/artUtils';
import { Color } from 'csstype';

const GridWrapper = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 10em;
`;

const GridRowAfterStripe = styled.span`
  width: 100%;
  height: 6.6em;
  border-top: 0.2em solid ${props => props.theme.highlightColor};
  border-bottom: 0.2em solid ${props => props.theme.highlightColor};
  overflow: hidden;
  
  &.force-show {
    display: block !important;
  }
  
  @media (max-width: 1023px) {
    display: none;
  }
`;

const GridBadgeWrapper = styled.div`
  position: absolute;
  top: 1em;
  left: 1em;
  z-index: 3;
  display: flex;
  flex-wrap: wrap;
  
  > span {
    margin-right: 1em;
    
    &:last-of-type {
      margin-right: 0;
    }
  }
`;

const GridImage = styled.figure`
  background-image: url(${props => props.theme.isRetina ? props.image['medium']: props.image['small']});  
`;

interface Props {
  image: Image;
  size?: 'medium' | 'small';
}

interface State {
  visible: boolean;
}

export class LazyGridImage extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      visible: false,
    };
  }

  render() {
    const {
      visible
    } = this.state;

    return (
      <Waypoint onEnter={this._handleOnEnter}>
        <GridImage
          className={visible ? 'lazy-image image-loaded' : 'lazy-image'}
          style={{
            backgroundImage: `url(${visible ? this.getRightImageSizeURL() : ''})`
          }}
          image={{}}
        />
      </Waypoint>
    );
  }

  private _handleOnEnter = () => {
    const img = new Image();
    img.onload = () => {
      this.setState({
        visible: true,
      });
    };
    img.src = this.getRightImageSizeURL();
  };

  private getRightImageSizeURL = (): string => {
    const {
      image: {
        medium,
        small,
      },
      size
    } = this.props;

    if (size) {
     return this.props.image[size];
    } else {
      if (isRetina()) {
        return medium;
      }

      return small;
    }
  }
}

const GridImageWrapper = styled.div`
  width: 100%;
  
  ${GridImage} {
    padding-top: 61.7%;
    background-size: cover;
    background-position: center;
    transition-property: filter, opacity;
    transition-timing-function: cubic-bezier(0.645, 0.045, 0.355, 1);
    transition-duration: 200ms;
    
    ${props => props.allowGrayscaleEffect ? `
      /* transition: 0.2s filter linear; */
      filter: grayscale(100%);
      ` : null }
    
    &.lazy-image {
      opacity: 0;
    }

    &.image-loaded {
      opacity: 1;
    }
  }
`;

const GridItem = styled.div`
  width: ${props => 25 * (props.itemSize || 1)}%;
  box-sizing: border-box;
  border-right: 0.1em solid ${props => props.theme.highlightColor};
  text-align: center;
  text-transform: uppercase;
  
  &:nth-of-type(4n+4) {
    border-right: none;  
  }
  
  a {
    position: relative;
    color: ${props => props.colorScheme === 'black' ? 'white' : 'black'};
    text-decoration: none;
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    background-color: ${props => props.colorScheme === 'black' ? 'black' : 'white'};
  }
  
  &:${props => props.theme.hoverProperty} {
    a {
      color: black;
      background-color: ${props => props.theme.highlightColor};
      
      ${GridImage} {
        filter: grayscale(0);
      }
    }
  }
  
  @media (max-width: 1023px) {
    width: 50%;  
  }
  
  @media (max-width: 600px) {
    width: 100%;
    border-right: none;
    border-bottom: 0.1em solid ${props => props.theme.highlightColor};
    flex-direction: row;
    
    /*a {
      flex-direction: row;
      
      ${GridImage} {
        
      }
    }*/
  }
`;

const GridItemContent = styled.div`
  display: flex;
  flex-direction: column;
  // justify-content: space-between;
  justify-content: space-around;
  flex-grow: 1;
  padding: 0 1em;
`;

const GridItemContentTitle = styled.h2`
  font-size: 6.8em !important;
  font-family: Flow-Inverted;
  line-height: 1.2;
  margin-top: ${em(7, 6.8)};
  margin-bottom: ${em(3, 6.8)};
  
  @media (max-width: 1920px) {
    font-size: ${68 / (1920 * 0.01)}vw !important;
  }
  
  @media (max-width: 1023px) {
    font-size: ${68 / (1023 * 0.01)}vw !important;
  }
  
  @media (max-width: 500px) {
    font-size: ${68 / (500 * 0.01)}vw !important;
  }
`;

const GridItemTagList = styled.ul`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  align-items: center;
  padding: 0 2em;
  margin-bottom: 2em;
  li {
    margin-right: 0.5em;
    
    &:last-child {
      margin-right: 0;
    }
    
    span {
      font-size: 1.6em;
      line-height: 1.5238095238095237em;
      font-family: Flow-Poster-Bold;
    }
  }
`;

export const renderList = (id: string, items: any, colorScheme?: string, itemSize?: number) => {
  if (items) {
    return (
      <GridWrapper>
        {items.map((item: any, index: number) => (
          _renderItem(id, items.length-1, item, index, colorScheme, itemSize)
        ))}
      </GridWrapper>
    );
  } else {
    return null;
  }
};

const _renderItem = (id: string, count: number, item: any, index: number, colorScheme?: string, itemSize?: number) => {
  return (
    <Fragment key={index}>
      {_renderGridItem(id, item, index, colorScheme, itemSize)}
      {(((index + 1) % 4 == 0) || index === count) &&
      <GridRowAfterStripe key={'stripe-' + index} className={index === count ? 'force-show' : ''}>
        <FlowStripe animate={true} color={'#000000'} />
      </GridRowAfterStripe>
      }
    </Fragment>
  );
};

const _renderGridItem = (id: string, item: any, index: number, colorScheme?: string, itemSize?: number) => {
  let badges: ReactNode[] = [];
  if (isNew(item.date)) {
    badges.push(<Badge type={'new'}/>);
  }

  if (id === 'art') {
    let artPartnerBadges: {name: string}[] = getArtTagBadges(item.tags);
    for (let i: number = 0; i < artPartnerBadges.length; i++) {
      let overrideBy: string = null;
      if (artPartnerBadges[i].name === 'FlowxOpera' || artPartnerBadges[i].name === 'FlowxOoppera') {
        overrideBy = 'ooppera';
      }
      let partnerBadgeLogo: string = '[' + (overrideBy ? overrideBy : artPartnerBadges[i].name).replace(/[\. /-]+/g, '').toUpperCase() + ']';
      const badgeColor: string | undefined = partnerBadgeLogo === '[PINKSPACE]' ? '#fdbbcb' : undefined;
      if (partnerBadgeLogo)
      badges.push(<Badge type={'partner'} content={partnerBadgeLogo} badgeColor={badgeColor}/>);
    }
  }

  if (item) {
    return (
      <GridItem key={index} colorScheme={colorScheme || 'white'} itemSize={itemSize || 1}>
        <Link to={ getRelativeUrl(item.link) }>
          {badges && badges.length > 0 &&
          <GridBadgeWrapper>
            {badges.map((badge: ReactNode, index: number) => (
              <Fragment key={index}>
                {badge}
              </Fragment>
            ))}
          </GridBadgeWrapper>
          }
          {(Boolean (item.images) && Boolean (item.images.landscape) && Boolean(item.images.landscape[0])) &&
          <GridImageWrapper allowGrayscaleEffect={id !== 'food' && id !== 'art' && id !== 'family' && id !== 'talk'}>
            <LazyGridImage image={item.images.landscape[0]} size={!itemSize || itemSize === 1 ? undefined : 'medium'} />
          </GridImageWrapper>
          }
          <GridItemContent>
            <GridItemContentTitle>{ item.title }</GridItemContentTitle>
            {item.tags &&
            <GridItemTagList>
              {item.tags.map((tag: any, index: number) => (
                <li key={index}>
                  <span>#{tag.name}</span>
                </li>
              ))}
            </GridItemTagList>
            }
          </GridItemContent>
        </Link>
      </GridItem>
    );
  } else {
    return null;
  }
};

export {
  GridWrapper,
  GridItem,
  GridItemContent,
  GridItemContentTitle,
  GridRowAfterStripe,
  GridBadgeWrapper,
  GridImageWrapper,
  GridImage
};



// WEBPACK FOOTER //
// /Users/ek1z/dev/flowfestival-com/src/components/grid/grid.tsx